import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Stack,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  GetApp as DownloadIcon,
  Straighten as WidthIcon,
  Error as DefectIcon,
  CheckCircle as SuccessIcon,
  Timeline as ChartIcon,
  Settings as SettingsIcon,
  AccessTime as TimeIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import {
  fabricWidthService,
  DashboardStats,
  FabricWidthBatch,
  FabricWidthData,
  FabricWidthDefect,
} from '../services/iot.service';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ChartTooltip, Legend, ResponsiveContainer, Area, AreaChart } from 'recharts';

const FabricWidthDashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedBatch, setSelectedBatch] = useState<FabricWidthBatch | null>(null);
  const [batchDetails, setBatchDetails] = useState<{
    batch: FabricWidthBatch;
    measurements: FabricWidthData[];
    defects: FabricWidthDefect[];
  } | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchDashboardStats();

    // Auto-refresh every 1 second for real-time updates (ESP32 sends every 500ms)
    const interval = setInterval(() => {
      if (autoRefresh) {
        fetchDashboardStats();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  // Auto-refresh batch details when dialog is open
  useEffect(() => {
    if (!openDialog || !selectedBatch) return;

    const refreshBatchDetails = async () => {
      try {
        const response = await fabricWidthService.getBatchDetails(
          selectedBatch.device_id,
          selectedBatch.batch_number
        );
        setBatchDetails(response.data);
      } catch (err: any) {
        console.error('Failed to refresh batch details:', err);
      }
    };

    // Refresh batch details every 1 second when dialog is open
    const interval = setInterval(refreshBatchDetails, 1000);

    return () => clearInterval(interval);
  }, [openDialog, selectedBatch]);

  const fetchDashboardStats = async () => {
    try {
      const response = await fabricWidthService.getDashboardStats();
      setStats(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch dashboard stats');
    }
  };

  const handleViewBatch = async (batch: FabricWidthBatch) => {
    try {
      setLoading(true);
      setSelectedBatch(batch);
      const response = await fabricWidthService.getBatchDetails(
        batch.device_id,
        batch.batch_number
      );
      setBatchDetails(response.data);
      setOpenDialog(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch batch details');
    } finally {
      setLoading(false);
    }
  };

  const handleExportBatch = async (deviceId: string, batchNumber: number) => {
    try {
      setLoading(true);
      const response = await fabricWidthService.exportBatchData(deviceId, batchNumber);
      
      // Create blob and download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `batch_${batchNumber}_${deviceId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to export batch data');
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteBatch = async (deviceId: string, batchNumber: string) => {
    if (!window.confirm(`Are you sure you want to delete batch ${batchNumber}? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoading(true);
      await fabricWidthService.deleteBatch(deviceId, batchNumber);
      setError(null);
      // Refresh dashboard stats to remove deleted batch
      await fetchDashboardStats();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete batch');
    } finally {
      setLoading(false);
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running':
        return 'success';
      case 'paused':
        return 'warning';
      case 'completed':
        return 'default';
      default:
        return 'default';
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  // Prepare chart data
  const getChartData = () => {
    if (!batchDetails) return [];
    
    // For real-time monitoring, show last 100 measurements (ESP32 sends every 500ms)
    // This represents approximately the last 50 seconds of data
    const recentData = batchDetails.measurements.slice(-100);
    
    return recentData.map(m => ({
      timestamp: format(new Date(m.received_at), 'HH:mm:ss'),
      width: Number(m.current_width.toFixed(3)),
      target: Number(m.target_width.toFixed(3)),
      defect: m.in_defect ? m.current_width : null,
    }));
  };

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h4" component="h1">
            <WidthIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Fabric Width Monitoring
          </Typography>
          <Box>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              onClick={() => setAutoRefresh(!autoRefresh)}
              sx={{ mr: 1 }}
            >
              Auto-Refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchDashboardStats}
            >
              Refresh
            </Button>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Active Devices
                    </Typography>
                    <Typography variant="h4">
                      {stats?.activeDevices || 0}
                    </Typography>
                  </Box>
                  <SettingsIcon color="primary" sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Running Batches
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats?.runningBatches || 0}
                    </Typography>
                  </Box>
                  <TimeIcon color="success" sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Defects Today
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {stats?.defectsToday || 0}
                    </Typography>
                  </Box>
                  <DefectIcon color="error" sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="body2">
                      Length Today (m)
                    </Typography>
                    <Typography variant="h4">
                      {(stats?.lengthToday || 0).toFixed(1)}
                    </Typography>
                  </Box>
                  <WidthIcon color="info" sx={{ fontSize: 40 }} />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Recent Batches */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Batches
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Batch #</TableCell>
                  <TableCell>Device</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Total Length (m)</TableCell>
                  <TableCell>Defects</TableCell>
                  <TableCell>Defect %</TableCell>
                  <TableCell>Run Time</TableCell>
                  <TableCell>Started At</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!stats || stats.recentBatches.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center">
                      <Typography variant="body2" color="textSecondary">
                        No batches found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  stats.recentBatches.map((batch) => (
                    <TableRow key={batch.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          #{batch.batch_number}
                        </Typography>
                      </TableCell>
                      <TableCell>{batch.device_name || batch.device_id}</TableCell>
                      <TableCell>
                        <Chip
                          label={batch.status.toUpperCase()}
                          color={getStatusColor(batch.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{batch.total_length.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip
                          label={batch.defect_count}
                          color={batch.defect_count > 0 ? 'error' : 'success'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={batch.defect_percentage > 5 ? 'error' : 'textPrimary'}
                        >
                          {batch.defect_percentage.toFixed(2)}%
                        </Typography>
                      </TableCell>
                      <TableCell>{formatDuration(batch.total_run_time)}</TableCell>
                      <TableCell>
                        {format(new Date(batch.started_at), 'MMM dd, HH:mm')}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewBatch(batch)}
                          >
                            <ChartIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Export CSV">
                          <IconButton
                            size="small"
                            onClick={() => handleExportBatch(batch.device_id, batch.batch_number)}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Batch">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteBatch(batch.device_id, batch.batch_number.toString())}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Batch Details Dialog */}
        <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="lg" fullWidth>
          <DialogTitle>
            Batch #{selectedBatch?.batch_number} - {selectedBatch?.device_name || selectedBatch?.device_id}
          </DialogTitle>
          <DialogContent>
            {loading ? (
              <LinearProgress />
            ) : batchDetails ? (
              <Box>
                {/* Batch Summary */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Status
                      </Typography>
                      <Chip
                        label={batchDetails.batch.status.toUpperCase()}
                        color={getStatusColor(batchDetails.batch.status)}
                        sx={{ mt: 1 }}
                      />
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Total Length
                      </Typography>
                      <Typography variant="h6">
                        {batchDetails.batch.total_length.toFixed(2)} m
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Defect Count
                      </Typography>
                      <Typography variant="h6" color={batchDetails.batch.defect_count > 0 ? 'error' : 'success.main'}>
                        {batchDetails.batch.defect_count}
                      </Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="body2" color="textSecondary">
                        Defect %
                      </Typography>
                      <Typography variant="h6" color={batchDetails.batch.defect_percentage > 5 ? 'error' : 'textPrimary'}>
                        {batchDetails.batch.defect_percentage.toFixed(2)}%
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Width Chart */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                  Width Measurements
                </Typography>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={getChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="timestamp" />
                    <YAxis domain={['dataMin - 0.1', 'dataMax + 0.1']} />
                    <ChartTooltip />
                    <Legend />
                    <Area
                      type="monotone"
                      dataKey="target"
                      stroke="#82ca9d"
                      fill="#82ca9d"
                      fillOpacity={0.2}
                      name="Target Width (m)"
                    />
                    <Line
                      type="monotone"
                      dataKey="width"
                      stroke="#8884d8"
                      dot={false}
                      name="Current Width (m)"
                    />
                    <Line
                      type="monotone"
                      dataKey="defect"
                      stroke="#ff0000"
                      dot={{ r: 3 }}
                      name="Defect"
                      connectNulls={false}
                    />
                  </AreaChart>
                </ResponsiveContainer>

                {/* Defects List */}
                {batchDetails.defects.length > 0 && (
                  <>
                    <Divider sx={{ my: 3 }} />
                    <Typography variant="h6" gutterBottom>
                      Defects ({batchDetails.defects.length})
                    </Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Defect #</TableCell>
                            <TableCell>Width (m)</TableCell>
                            <TableCell>Length (m)</TableCell>
                            <TableCell>Severity</TableCell>
                            <TableCell>Detected At</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {batchDetails.defects.map((defect) => (
                            <TableRow key={defect.id}>
                              <TableCell>{defect.defect_number}</TableCell>
                              <TableCell>{defect.width.toFixed(3)}</TableCell>
                              <TableCell>{defect.defect_length.toFixed(3)}</TableCell>
                              <TableCell>
                                <Chip
                                  label={defect.severity.toUpperCase()}
                                  color={getSeverityColor(defect.severity)}
                                  size="small"
                                />
                              </TableCell>
                              <TableCell>
                                {format(new Date(defect.detected_at), 'HH:mm:ss')}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Box>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>Close</Button>
            {selectedBatch && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => handleExportBatch(selectedBatch.device_id, selectedBatch.batch_number)}
              >
                Export CSV
              </Button>
            )}
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default FabricWidthDashboard;
